import {
  Dashboard,
  DashboardConfig,
  DashboardIdAndPageId,
  Dashboards,
  PageConfig,
} from '../../common/components/dashboards/types';
import { Domains } from '../../constants';
import { DataFieldWithDataType } from '../../types';
import { AllowedPatterns } from '../dataupload/object-key-patterns';
import { Hasher } from '../hasher';
import {
  AssignedPermissions,
  Permission,
  PermissionTypeOf,
  PermissionTypes,
} from '../permission/types';
import { getFilterFieldFromTag } from '../permission/utils';
import { HasDependencies, hashString } from '../types';
import { ReadData, ViewDashboards } from '../types-graphql';
import { DomainViewAuthorizationService } from './domain-view-authorization-service';

export interface ExecutorRoleViewAuthorizationService extends HasDependencies {
  canSeeField: (field: DataFieldWithDataType) => boolean;
  canSeeFilter: (field: DataFieldWithDataType) => boolean;

  // dashboard
  canSeeDashboard: (dbId: string) => boolean;
  canSeeDashboardPage: (page: DashboardIdAndPageId) => boolean;
  canSeeDataView: (dashboard: Dashboards) => boolean;
  canExportDataView: (dashboard: Dashboards) => boolean;
  canSeeDataExplorer: (dashboard: Dashboards) => boolean;
  canExportDataExplorer: (dashboard: Dashboards) => boolean;
  canExportChartView: (dashboard: Dashboards) => boolean;

  // custom dashboard
  // panalytSuperAdmin features (can move this to an enabledFeaturesService if need be later)
  canSeeCustomDashboard: (customDb: DashboardConfig) => boolean;
  canSeeCustomDashboardPage: (customDbPage: PageConfig) => boolean;

  //SUPER ADMIN
  canManageThisCompany: () => boolean;
}

export interface OriginalRoleViewAuthorizationService extends HasDependencies {
  canManageAllDomains(): boolean;
}

export interface EffectiveRoleViewAuthorizationService extends HasDependencies {
  // PANALYT SUPER ADMIN
  canManageAllDomains: () => boolean;
  canSeeChartDevOptions: () => boolean;
  canSeeSuperAdminMenu: () => boolean;
  canSeePanalytSuperAdminMenu: () => boolean;

  // SUPER ADMIN
  canManageThisDomain: () => boolean;

  canUploadClientRawData: () => boolean;
  canDeleteClientRawData: () => boolean;
  canDownloadClientRawData: () => boolean;
  canReadClientRawData: () => boolean;

  // Effective object-key patterns for client-file operations.
  // `null` = unrestricted, `[]` = no access (see object-key-patterns.ts).
  allowedUploadClientRawDataPatterns: () => AllowedPatterns;
  allowedDeleteClientRawDataPatterns: () => AllowedPatterns;
  allowedDownloadClientRawDataPatterns: () => AllowedPatterns;

  // permission pages
  canReadUserPermissions(): boolean;
  canWriteUserPermissions(): boolean;
  canReadRoleSettings(): boolean;
  canWriteRoleSettings(): boolean;

  canManageAliases: () => boolean;
  canManageOwnUserAccount: () => boolean;

  canDisableLoginAllUserRoles: () => boolean;
  canEnableLoginAllUserRoles: () => boolean;
}

export class EffectiveRoleViewAuthorizationServiceImpl
  implements EffectiveRoleViewAuthorizationService
{
  constructor(
    readonly domain: Domains,
    readonly effectivePermissions: AssignedPermissions<Permission>
  ) {}

  public getDeps = (): (string | undefined)[] => [
    hashString([this.domain, Hasher.hash(this.effectivePermissions)]),
  ];

  public canUploadClientRawData(): boolean {
    return this.canDoForDomain(PermissionTypes.WriteClientFile);
  }

  public canDeleteClientRawData(): boolean {
    return this.canDoForDomain(PermissionTypes.DeleteClientFile);
  }

  public canDownloadClientRawData(): boolean {
    return this.canDoForDomain(PermissionTypes.DownloadClientFile);
  }

  public canReadClientRawData(): boolean {
    return this.canDoForDomain(PermissionTypes.ReadClientFile);
  }

  public allowedUploadClientRawDataPatterns = (): AllowedPatterns => {
    return this.clientFilePatternsFor(PermissionTypes.WriteClientFile);
  };

  public allowedDeleteClientRawDataPatterns = (): AllowedPatterns => {
    return this.clientFilePatternsFor(PermissionTypes.DeleteClientFile);
  };

  public allowedDownloadClientRawDataPatterns = (): AllowedPatterns => {
    return this.clientFilePatternsFor(PermissionTypes.DownloadClientFile);
  };

  private clientFilePatternsFor = (
    permissionType:
      | PermissionTypes.ReadClientFile
      | PermissionTypes.WriteClientFile
      | PermissionTypes.DownloadClientFile
      | PermissionTypes.DeleteClientFile
  ): AllowedPatterns => {
    const patternLists = this.effectivePermissions
      .validFor(this.domain)
      .filterByType(permissionType)
      .map((ap) => ap.permission.allowedPatterns ?? null);
    if (patternLists.length === 0) {
      // No permission granted at all: no key is accessible.
      return [];
    }
    if (patternLists.some((patterns) => patterns === null)) {
      // At least one unrestricted grant covers everything.
      return null;
    }
    return patternLists.flatMap((patterns) => patterns ?? []);
  };

  // TODO: Valentin permissions
  public canManageOwnUserAccount = (): boolean => {
    return (
      this.canDoForDomain(PermissionTypes.ManageCompany) &&
      this.canDoForDomain(PermissionTypes.ManageUsers)
    );
  };

  public canManageAliases = (): boolean => {
    return this.canDoForDomain(PermissionTypes.ManageCompany);
  };

  // TODO: Valentin permissions
  public canReadUserPermissions = (): boolean => {
    // all superadmins
    return this.canManageAllDomains() || this.canManageThisCompany();
  };

  // TODO: Valentin permissions
  public canWriteUserPermissions = (): boolean => {
    // all superadmins
    return this.canManageAllDomains() || this.canManageThisCompany();
  };

  // TODO: Valentin permissions
  public canReadRoleSettings = (): boolean => {
    // all superadmins
    return this.canManageAllDomains() || this.canManageThisCompany();
  };

  // TODO: Valentin permissions
  public canWriteRoleSettings = (): boolean => {
    // all superadmins
    return this.canManageAllDomains() || this.canManageThisCompany();
  };

  // TODO: Valentin permissions - should this be canManageThisCompany?
  public canSeeSuperAdminMenu = (): boolean => {
    return (
      this.canDoForDomain(PermissionTypes.ManageCompany) ||
      this.canDoForDomain(PermissionTypes.ManageUsers) ||
      this.canDoForDomain(PermissionTypes.ReadClientFile) ||
      this.canDoForDomain(PermissionTypes.WriteClientFile)
    );
  };

  // TODO: Valentin permissions
  public canManageThisCompany() {
    return this.canDoForDomain(PermissionTypes.ManageCompany);
  }

  public canSeePanalytSuperAdminMenu = (): boolean => {
    return this.canManageAllDomains();
  };

  public canSeeChartDevOptions = () => {
    return this.canManageAllDomains();
  };

  public canManageAllDomains = () => {
    // PanalytSuperAdmin
    return !!this.getManageDomainPermission().map(
      (ap) => ap.permission.domain === null
    ).length;
  };

  public canDisableLoginAllUserRoles = () => {
    return this.canManageAllDomains();
  };

  public canEnableLoginAllUserRoles = () => {
    return this.canManageAllDomains();
  };

  public canManageThisDomain = () => {
    return this.canDoForDomain(PermissionTypes.ManageDomain);
  };

  private canDoForDomain = (permissionType: PermissionTypes) => {
    return this.effectivePermissions
      .validFor(this.domain)
      .filterByType(permissionType)
      .nonEmpty();
  };

  // TODO: Valentin permissions
  private getManageDomainPermission = () => {
    return this.effectivePermissions.filterByType(PermissionTypes.ManageDomain);
  };
}

/**
 * Implementation that always uses the original user role permissions (ignoring role simulation)
 */
export class OriginalRoleViewAuthorizationServiceImpl
  implements OriginalRoleViewAuthorizationService
{
  constructor(readonly permissions: AssignedPermissions<Permission>) {}

  public getDeps = (): (string | undefined)[] => [
    hashString([Hasher.hash(this.permissions)]),
  ];

  public canManageAllDomains = () => {
    // PanalytSuperAdmin
    return !!this.getManageDomainPermissions().map(
      (ap) => ap.permission.domain === null
    ).length;
  };

  // TODO: Valentin permissions
  private getManageDomainPermissions = () => {
    return this.permissions.filterByType(PermissionTypes.ManageDomain);
  };
}

export class ExecutorRoleViewAuthorizationServiceImpl
  implements ExecutorRoleViewAuthorizationService
{
  constructor(
    readonly domain: Domains,
    readonly domainViewAuthorizationService: DomainViewAuthorizationService,
    readonly executorRolePermissions: AssignedPermissions<Permission>,
    readonly allDashboards: Dashboard[]
  ) {}

  public getDeps = (): (string | undefined)[] => [
    hashString([
      this.domain,
      ...this.domainViewAuthorizationService.getDeps(),
      Hasher.hash(this.executorRolePermissions),
      Hasher.hash(this.allDashboards),
    ]),
  ];

  public canSeeFilter = (field: DataFieldWithDataType): boolean => {
    const { dataType, dataField } = field;
    const useFilterPermission =
      this.getUseFiltersPermisions().first()?.permission;

    if (!useFilterPermission) {
      return false;
    } else {
      return (
        useFilterPermission.allowedTags
          ?.map((t) => getFilterFieldFromTag(t))
          .deepCompareContains({ dataType, dataField }) ?? true
      );
    }
  };

  public canSeeField = (field: DataFieldWithDataType): boolean => {
    return this.isFieldAllowed(
      field,
      this.getReadDataPermissions().first()?.permission
    );
  };

  public canSeeDashboard = (dbId: string) => {
    return this.isDashboardAllowed(
      dbId,
      this.getViewDashboardPermissions().first()?.permission
    );
  };

  public canSeeDashboardPage = (page: DashboardIdAndPageId) => {
    const dashboard = this.getDashboardFromDashboardPage(page);
    const isDashboardAllowedForDomain =
      dashboard.enabled &&
      this.domainViewAuthorizationService.isDashboardAllowed(dashboard);
    const isDashboardAllowedInPermissions = this.isDashboardPageAllowed(
      page,
      this.getViewDashboardPermissions().first()?.permission
    );
    return isDashboardAllowedForDomain && isDashboardAllowedInPermissions;
  };

  public canSeeDataView = (dashboard: Dashboards) => {
    return this.getViewDashboardPermissions().some(
      (ap) =>
        ap.permission.viewDataView === null ||
        ap.permission.viewDataView.includes(dashboard)
    );
  };

  public canExportDataView = (dashboard: Dashboards) => {
    return this.getViewDashboardPermissions().some(
      (ap) =>
        ap.permission.exportDataView === null ||
        ap.permission.exportDataView.includes(dashboard)
    );
  };

  // Data Explorer (PBS cell drill-down) exposes low-level employee data
  // (e.g. disabilities, salaries), gated by the underlying Data View
  // permission. Confidential cells (small-group aggregates) are excluded
  // separately at render time, regardless of this permission.
  public canSeeDataExplorer = (dashboard: Dashboards) => {
    return this.canSeeDataView(dashboard);
  };

  public canExportDataExplorer = (dashboard: Dashboards) => {
    return this.canExportDataView(dashboard);
  };

  public canExportChartView = (dashboard: Dashboards) => {
    return this.getViewDashboardPermissions().some(
      (ap) =>
        ap.permission.exportChartView === null ||
        ap.permission.exportChartView.includes(dashboard)
    );
  };

  public canSeeCustomDashboard = (customDb: DashboardConfig) => {
    if (customDb.superAdminOnly && !this.canManageThisCompany()) {
      return false;
    }
    return true;
  };

  public canSeeCustomDashboardPage = (customDbPage: PageConfig) => {
    return true;
  };

  public canManageThisCompany = () => {
    // SuperAdmin
    return this.getManageCompanyPermission().nonEmpty();
  };

  private isFieldAllowed = (
    field: DataFieldWithDataType,
    permission?: ReadData
  ) => {
    const hasFullAccess = permission?.access === null ?? false;
    const hasSpecificFieldAccess =
      permission?.access?.some(
        (a) =>
          a.datatype === field.dataType &&
          (a.fields?.specificDatafields?.some(
            (f) => f.datafield === field.dataField
          ) ??
            false)
      ) ?? false;
    return hasFullAccess || hasSpecificFieldAccess;
  };

  private isDashboardAllowed = (dbId: string, permission?: ViewDashboards) => {
    if (permission && permission.dashboardPages === null) {
      return true;
    } else {
      const allowedDashboards =
        permission?.dashboardPages?.map((dbp) => dbp.dashboard) ?? [];
      return allowedDashboards.includes(dbId);
    }
  };

  private getDashboardFromDashboardPage = (
    page: DashboardIdAndPageId
  ): Dashboard => {
    const dashboard = this.allDashboards.find((db) =>
      db.pages.some((p) => p.pageId === page.pageId)
    ) as Dashboard;
    return dashboard;
  };

  private isDashboardPageAllowed = (
    page: DashboardIdAndPageId,
    permission?: ViewDashboards
  ) => {
    const dashboardPages = permission?.dashboardPages;
    if (dashboardPages === null) {
      return true;
    } else {
      return Boolean(
        dashboardPages?.find(
          (dbp) =>
            dbp.dashboard === page.dashboardId &&
            (dbp.pages === null || dbp.pages.includes(page.pageId))
        )
      );
    }
  };

  private getViewDashboardPermissions = () => {
    return this.getPermissionForDomain(PermissionTypes.ViewDashboards);
  };

  private getReadDataPermissions = () => {
    return this.getPermissionForDomain(PermissionTypes.ReadData);
  };

  private getUseFiltersPermisions = () => {
    return this.getPermissionForDomain(PermissionTypes.UseFilters);
  };

  private getManageCompanyPermission = () => {
    return this.getPermissionForDomain(PermissionTypes.ManageCompany);
  };
  public isCustomDashboardAllowedInExecutorRole = (
    customDb: DashboardConfig
  ) => {
    // For now, we are making all storyboards available to all users on all domains
    return true; //this.userCanManageThisCompany();
  };

  private getPermissionForDomain = <PT extends PermissionTypes>(
    permissionType: PT
  ): AssignedPermissions<PermissionTypeOf<PT>> => {
    return this.executorRolePermissions
      .validFor(this.domain)
      .filterByType(permissionType);
  };
}
